/**
 * useChatStream — the SSE chat-stream hook (STORY-002-009).
 *
 * Per Implementation Addendum §7.3.1: the chat stream uses
 * `fetch(...)` + `ReadableStream`, NOT `EventSource`. EventSource cannot POST
 * a request body and cannot set an `Authorization` header, both of which the
 * `/chat/stream` contract (STORY-002-004) requires.
 *
 * Wire format (mirrors `apps/api/garden/chat/sse.py`):
 *
 *     event: <name>\n
 *     data: <json>\n
 *     \n
 *
 * with keep-alive comment lines (`: keepalive\n\n`) interleaved. Event names:
 *   intent    {intent, confidence}
 *   retrieval {kb_used: []}                 (RAG placeholder — EPIC-004)
 *   token     {value}
 *   products  {items: [...]}                (marketplace product cards AND/OR
 *                                            quote `seller_match` cards — design
 *                                            revamp; discriminated by `kind`)
 *   citation  {slug, title, lang}           (KB article the answer is grounded in)
 *   followups {items: [str, ...]}           (2-3 suggested next questions)
 *   clarify   {round_id, kind, questions,   (a clarifying-question round, every
 *              allow_skip}                   kind — contract C8)
 *   attachment_status {state:"reading"}     (assistant is reading the
 *                                            attached document(s) — chat docs)
 *   materials {id, status, materials:[...], (the sourced quote for an
 *              summary:{...}, ...}            attachment turn — QuoteResponse)
 *   stage     {stage, current?, total?}     (SCRUM-281 — the pipeline step
 *                                            running RIGHT NOW; a key, never
 *                                            text; current/total only on
 *                                            reading_document)
 *   done      {tokens_input, tokens_output, latency_total_ms, ...}
 *   error     {code, intent?, ...}
 *
 * Auth (Addendum §7.5, REQ-PRD-§11.2-A):
 *   - The access token is read from the in-memory auth store on each send;
 *     it is NEVER read from or written to localStorage.
 *   - A `Bearer` header + a per-send UUIDv7 `Idempotency-Key` are attached.
 *   - On a 401 BEFORE any token is emitted, the hook attempts ONE refresh
 *     (POST /auth/refresh, the same contract the api-client uses) and retries
 *     the send once. A persistent 401 clears auth and redirects to
 *     `/login?return_to=…` via the api-client's configured callback.
 *   - Guest mode (`SendOptions.guest`, SCRUM-273): the guest token, the guest
 *     refresh, the contract's 403 refusals (`guest_*` codes) and the
 *     `allowance` frame; a guest is never redirected.
 *
 * SSE no-retry-after-token (Addendum §8.1.1): once a `token` event has been
 * emitted, a mid-stream failure is surfaced as a terminal error — the stream
 * is never silently retried.
 *
 * Idempotency-Key (Addendum §6.3 SSE edge case): ONE key per `send()` call,
 * NOT one per chunk. A retried send (e.g. after refresh) reuses the same key
 * so the server dedupes the persisted message row.
 */
import { useCallback, useEffect, useRef } from "react";

import {
  attemptGuestRefresh,
  attemptRefresh,
  generateUuidV7,
  getApiClientConfig,
  type RefreshOutcome,
} from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import type { SupportedLanguage } from "@/lib/i18n";
import type { MaterialItem, QuoteSummary } from "@/lib/garden-api";
import {
  guestStreamErrorCode,
  parseGuestAllowance,
  readGuestRefusal,
  type GuestAllowance,
  type GuestLimitReason,
} from "@/features/chat/guest/allowance";
import { GuestSessionError, ensureGuestSession } from "@/features/chat/guest/api";
import { parseClarifyRound } from "@/features/chat/clarify/parse";
import type { ClarifyAnswerInput, ClarifyRoundEvent } from "@/features/chat/clarify/types";

export const CHAT_STREAM_PATH = "/chat/stream";

/** Decoded SSE frame: an event name + the parsed JSON `data` payload. */
export interface SseFrame {
  event: string;
  data: Record<string, unknown>;
}

/** `event: intent` payload. */
export interface IntentEvent {
  intent: string;
  confidence: number;
}

/** Terminal `event: error` payload. */
export interface StreamErrorEvent {
  code: string;
  intent?: string;
  message?: string;
  retryAfterSeconds?: number;
  /** The targeted Need's id, when the error occurred on a Smart
   * Recommendations R1 `fetch_need` turn (e.g. `smart_rec.need_not_found`). */
  needId?: string;
  /** Guest refusals (contract): why the limit hit (`messages|tokens|budget`). */
  guestReason?: GuestLimitReason;
  /** Guest refusals: the allowance the refusal body carried. */
  allowance?: GuestAllowance;
}

/** The chat turn's action. Mirrors the backend `ChatAction` Literal
 * (`garden/chat/schemas/stream.py`). `"chat"` is the ordinary typed/attachment
 * turn; `"fetch_need"` taps ONE Recommendation-Plan Need (Smart
 * Recommendations R1); `"clarify_answer"` answers or skips a clarifying-question
 * round (contract C7) — no classification, no gate. */
export type ChatAction = "chat" | "fetch_need" | "clarify_answer";

/** One Recommendation-Plan Need's retrieval hint (Smart Recommendations R1).
 * Mirrors the backend `_NEED_JSON_SCHEMA` `retrieval` shape
 * (`garden/chat/orchestrator.py`) 1:1 — snake_case wire fields. */
export interface NeedRetrieval {
  category_term: string | null;
  category_confidence: number;
  query_text: string;
  keywords: string[];
}

/**
 * One concrete product need extracted from a renovation-advice answer
 * (Smart Recommendations R1). Mirrors the backend `_NEED_JSON_SCHEMA` /
 * `orchestrator._normalize_need` shape 1:1. Labels/`why` are ALREADY in the
 * conversation language — render verbatim, never re-translated client-side.
 */
export interface Need {
  need_id: string;
  label: string;
  why: string;
  priority: number;
  retrieval: NeedRetrieval;
  needs_clarification: boolean;
  facet_group: string | null;
}

/**
 * `event: plan` payload — the Recommendation Plan extracted from THIS turn's
 * answer (Smart Recommendations R1). Mutually exclusive with `followups` on
 * the SAME turn — the backend never emits both (one suggestion surface).
 */
export interface PlanEvent {
  items: Need[];
}

/**
 * One product card surfaced inside a chat assistant turn (demo slice). Mirrors
 * the backend marketplace `SearchResultItem` / `chat_messages.cards_json` entry
 * 1:1, so the same shape renders whether a card arrives live on the `products`
 * event or rehydrated from a persisted thread message.
 */
export interface ProductCardData {
  id: string;
  name: string;
  price_kgs: string | number | null;
  /**
   * The high end of a price BAND, and which of the three shapes the pair is
   * (`exact` | `range` | `tiered`, v0052). `price_kgs` alone is the LOW end,
   * so a card rendering it by itself quotes 5 500 for a product that sells at
   * 5 500–14 000. Optional because a thread persisted before v0052 rehydrates
   * without them — an absent max is simply an exact price.
   */
  price_max_kgs?: string | number | null;
  price_kind?: string | null;
  /**
   * Seller contact the buyer may be SHOWN, already resolved server-side
   * (v0053). Never the raw `sellers.phone_e164`: 9 of 79 QA sellers carry a
   * sentinel minted to clear the unique phone index, and all of them read as
   * `verified`. Both absent means render no contact affordance at all.
   * Optional because a thread persisted before v0053 rehydrates without them.
   */
  seller_phone_e164?: string | null;
  seller_whatsapp_e164?: string | null;
  stock_state: string;
  seller_id: string;
  seller_business_name: string;
  market_id: string;
  market_name_ru: string;
  pavilion_number: string;
  photo_thumb_url: string;
}

/**
 * One quote `seller_match` card surfaced inside an assistant quote-review turn
 * (design revamp — the `SellerMiniCard` row under "Разобрал смету №142…").
 *
 * Rides the SAME `products` event as the marketplace product cards but carries
 * the discriminant `kind: "seller_match"` and a different (price + unit + seller
 * id) subset — the rich display fields (rating / district / avatar colour) are
 * resolved front-end-side from the seller-profile surface, exactly as the
 * backend `_seller_cards_for_material` docstring describes
 * (`apps/api/garden/chat/orchestrator.py`). `price_kgs` is the Decimal-as-JSON
 * **string** demo convention (parse with `parseDecimal`).
 */
export interface SellerMatchCardData {
  kind: "seller_match";
  seller_id: string;
  business_name: string;
  /** Decimal → string. */
  price_kgs: string;
  /** Localized unit label (e.g. "м²", "уп") already picked server-side. */
  unit: string;
  /** True for the cheapest match (rendered in the success colour). */
  cheapest: boolean;
}

/** `event: citation` payload — the KB article the answer is grounded in. */
export interface CitationEvent {
  slug: string;
  title: string;
  lang: string;
}

/** `event: followups` payload — 2-3 suggested next questions. */
export interface FollowupsEvent {
  items: string[];
}

/**
 * `event: attachment_status` payload — the assistant is reading the attached
 * document(s) (chat document attachments). Streamed before the summary tokens
 * so the UI can show a "Reading…" working bubble.
 */
export interface AttachmentStatusEvent {
  state: "reading";
}

/**
 * `event: stage` payload (SCRUM-281) — the pipeline step the server says is
 * running RIGHT NOW, emitted immediately before that step starts. A key, never
 * text: `chat-stage.ts` maps it to the localised label. `current`/`total` ride
 * only on `reading_document` (file i of n) and are kept only when both are
 * positive integers with `current <= total`.
 */
export interface StageEvent {
  key: string;
  current?: number;
  total?: number;
}

/**
 * `event: materials` payload — the sourced quote (the same `QuoteResponse`
 * shape the `/quotes` endpoint returns) produced when a document turn's
 * extracted line-items are sourced against the marketplace (chat document
 * attachments). Renders inline as the `ChatMaterialsTable`. The backend ALSO
 * persists this on the assistant message's `cards_json` as a single
 * `{kind:"materials", quote:<this>}` entry so a thread reload re-renders it.
 * Money fields are the Decimal-as-JSON **string** convention (parse + format).
 */
export interface MaterialsEvent {
  id: string;
  project_id: string | null;
  upload_id: string | null;
  status: string;
  quote_number: string | null;
  contractor_name: string | null;
  quote_date: string | null;
  materials: MaterialItem[];
  summary: QuoteSummary;
}

/**
 * Type guard: is a `products` item a quote `seller_match` card (vs a
 * marketplace product card)? The marketplace cards have no `kind` field, so a
 * `kind === "seller_match"` discriminant is unambiguous.
 */
export function isSellerMatchCard(
  item: ProductCardData | SellerMatchCardData,
): item is SellerMatchCardData {
  return (
    typeof item === "object" &&
    item !== null &&
    (item as { kind?: unknown }).kind === "seller_match"
  );
}

export interface UseChatStreamCallbacks {
  /** Fired for EVERY parsed frame, of any type (its event name), before the
   * frame's own callback — never for a keep-alive comment, which is not a
   * frame. Lets the caller know the turn is no longer waiting for its first
   * frame (the «Соединяемся…» hint, owner-approved 2026-10-13). */
  onFrame?: (event: string) => void;
  /** Fired on `event: thread` — the server's thread id for THIS turn. The
   * client must echo it back as `threadId` on subsequent turns so the
   * conversation continues one thread (server-side document grounding +
   * multi-turn memory are thread-scoped — without this every turn starts a
   * new thread and follow-ups can't see an earlier attachment). */
  onThreadId?: (threadId: string) => void;
  /** Fired on `event: intent` — the classifier decision (AC #3). */
  onIntent?: (event: IntentEvent) => void;
  /** Fired on each `event: token` — one streamed token (AC #2). */
  onToken?: (value: string) => void;
  /** Fired on `event: products` — marketplace cards for a product-search turn.
   * NEVER fired when the frame also carries a `need_id` (Smart
   * Recommendations R1) — that case fires `onNeedProducts` instead. */
  onProducts?: (items: ProductCardData[]) => void;
  /** Fired on `event: products` carrying quote `seller_match` cards (design). */
  onSellerCards?: (items: SellerMatchCardData[]) => void;
  /** Fired on `event: citation` — the grounding KB article (design). */
  onCitation?: (event: CitationEvent) => void;
  /** Fired on `event: retrieval` — the REAL KB sources (kb_used[]) the answer is
   * grounded in (Phase 2). Empty list = ungrounded (not fired). */
  onRetrieval?: (sources: CitationEvent[]) => void;
  /** Fired on `event: followups` — suggested next questions (design). */
  onFollowups?: (items: string[]) => void;
  /** Fired on `event: plan` — the Recommendation Plan for THIS turn (Smart
   * Recommendations R1). Mutually exclusive with `onFollowups`. */
  onPlan?: (items: Need[]) => void;
  /** Fired on `event: clarify` — a clarifying-question round of any kind
   * (contract C8): ordinary, quote, or a smart-rec R1/R2 question. Only a
   * payload `parseClarifyRound` accepts is dispatched. */
  onClarify?: (round: ClarifyRoundEvent) => void;
  /** Fired on `event: products` WHEN the frame carries a `need_id` (Smart
   * Recommendations R1) — a deterministic Need-scoped fetch (a chip tap, OR
   * a typed turn the orchestrator resolved against the plan), as opposed to
   * the general product-search `onProducts`. */
  onNeedProducts?: (needId: string, items: ProductCardData[]) => void;
  /** Fired on `event: attachment_status` — the assistant is reading the
   * attached document(s) (chat document attachments). */
  onAttachmentStatus?: (event: AttachmentStatusEvent) => void;
  /** Fired on `event: stage` — the step the server is about to run
   * (SCRUM-281). An unknown key is passed through; the UI renders it as the
   * generic "working" label. */
  onStage?: (event: StageEvent) => void;
  /** Fired on `event: materials` — the sourced quote rendered inline as the
   * in-chat materials table (chat document attachments). */
  onMaterials?: (event: MaterialsEvent) => void;
  /** Fired on `event: allowance` — guest turns only, right before `done`
   * (contract SSE). Member streams never carry it. */
  onAllowance?: (allowance: GuestAllowance) => void;
  /** Fired on `event: error` OR a network/transport failure (AC #5/#6/#13). */
  onError?: (event: StreamErrorEvent) => void;
  /** Fired on `event: done` — stream completed cleanly (AC #4). */
  onDone?: (payload: Record<string, unknown>) => void;
}

export interface SendOptions {
  threadId?: string | null;
  language: SupportedLanguage;
  /**
   * Ready attachment upload ids for this turn (chat document attachments).
   * Threaded into the request body's `attachments` so the orchestrator can
   * fetch + understand the clean S3 objects. Empty for a text-only turn.
   */
  attachmentUploadIds?: string[];
  /**
   * Recent prior USER message texts (oldest→newest), sent so the backend's
   * deterministic product-search can resolve a product noun mentioned earlier
   * in the conversation. Demo slice.
   */
  history?: string[];
  /**
   * `"quote_starter"` was sent ONLY for a turn produced by the "Parse my
   * quote" chat-starter card, never for a message the user typed. It told the
   * backend to go straight to the upload prompt instead of surfacing a
   * previously stored quote. An organic typed follow-up (e.g. "разбери мою
   * смету") omits it and keeps finding the stored quote.
   *
   * The starter list is on the empty chat (removed by the owner on 2026-10-08,
   * restored on 2026-10-09), but since SCRUM-146 its quote row opens the
   * composer's file picker instead of sending, so no caller sets this today.
   * The field and the backend's `is_quote_starter` guard stay, harmless.
   */
  promptOrigin?: string;
  /**
   * Smart Recommendations R1 (spec §4): `"fetch_need"` taps ONE
   * Recommendation-Plan Need — no classification, no specialist call.
   * Defaults to the ordinary `"chat"` turn.
   */
  action?: ChatAction;
  /** The tapped Need's id (`action: "fetch_need"` only; required by the
   * backend schema when set). */
  needId?: string;
  /** An optional facet hint narrowing a `fetch_need` retrieval (e.g. a
   * "cheaper" / "same brand" chip). Ignored on an ordinary `"chat"` turn.
   * R1's FREE-TEXT semantic hint — unrelated to the R2 fields below, which are
   * a structured attribute filter. Both may ride the same turn. */
  facet?: string;
  /** R2 Phase B — the attribute field the user narrowed on (e.g. `material`).
   * Sent together with `facetValue`; both come VERBATIM from the chosen
   * option's `filter`. */
  facetField?: string;
  /** R2 Phase B — the option slug the user picked (e.g. `ceramic`). */
  facetValue?: string;
  /** R2 Phase B — "just show me everything". REQUIRED on skip: without it the
   * replayed turn is indistinguishable from one that never answered, so the
   * server asks the same question again, forever. */
  facetSkip?: boolean;
  /** Clarifying-question rounds (contract C7): the round this turn answers —
   * a `"clarify_answer"` turn, or a smart-rec `"fetch_need"` pick or skip. */
  roundId?: string;
  /** `"clarify_answer"` only: the answers, in round order (empty on a skip). */
  clarifyAnswers?: ClarifyAnswerInput[];
  /** «Пропустить — просто ответь» on a `"clarify_answer"` turn; also the
   * marker on the FIRST fetch of a smart-rec R1 skip (W4). Sent only when true. */
  clarifySkip?: boolean;
  /**
   * Guest mode (SCRUM-273). With no member token in memory the send first
   * ensures a guest session (created on the first send, never on page load),
   * then streams with the guest token; a guest 401 runs the guest refresh and
   * never the /login redirect. A member streams as the member regardless.
   */
  guest?: boolean;
}

export interface UseChatStreamResult {
  send: (message: string, options: SendOptions) => Promise<void>;
  /** Abort the live stream (AC #7). Discarding partial state is the caller's. */
  abort: () => void;
  /**
   * Mute the live send WITHOUT aborting its request (final fix wave, I1): the
   * caller has moved to another conversation, so nothing the send decodes
   * from now on is dispatched, while the server is left to finish — and
   * save — the turn it is producing. A detached send is no longer "the live
   * stream": a later `abort()`, and unmount, reach only the sends after it.
   * Returns whether a send was live (and is now muted).
   */
  detach: () => boolean;
}

interface ChatStreamRequestBody {
  message: string;
  thread_id: string | null;
  language: SupportedLanguage;
  attachments: string[];
  history: string[];
  prompt_origin?: string;
  action?: ChatAction;
  need_id?: string;
  facet?: string;
  facet_field?: string;
  facet_value?: string;
  facet_skip?: boolean;
  round_id?: string;
  clarify_answers?: ClarifyAnswerInput[];
  clarify_skip?: boolean;
}

/**
 * Parse a buffer of decoded SSE text into complete frames, returning the
 * frames plus the trailing (incomplete) remainder to carry into the next
 * chunk. Frames are separated by a blank line (`\n\n`). Comment lines (those
 * beginning with `:`, e.g. keep-alives) are ignored per the SSE spec. A
 * `data:` field may span multiple lines; they are joined with `\n`.
 *
 * Exported for direct unit testing of the framing logic.
 */
export function parseSseBuffer(buffer: string): {
  frames: SseFrame[];
  remainder: string;
} {
  const frames: SseFrame[] = [];
  // Normalise CRLF to LF so the blank-line boundary is consistent.
  const normalised = buffer.replace(/\r\n/g, "\n");
  const blocks = normalised.split("\n\n");
  // The last element is the (possibly empty) incomplete tail.
  const remainder = blocks.pop() ?? "";

  for (const block of blocks) {
    if (block.length === 0) continue;
    let eventName = "message";
    const dataLines: string[] = [];
    for (const rawLine of block.split("\n")) {
      const line = rawLine;
      if (line.length === 0 || line.startsWith(":")) {
        // Blank or comment (keep-alive) line — skip.
        continue;
      }
      if (line.startsWith("event:")) {
        eventName = line.slice("event:".length).trim();
      } else if (line.startsWith("data:")) {
        // A single leading space after the colon is stripped per spec.
        let value = line.slice("data:".length);
        if (value.startsWith(" ")) value = value.slice(1);
        dataLines.push(value);
      }
    }
    if (dataLines.length === 0) {
      // An event with no data (rare) — skip rather than emit a junk frame.
      continue;
    }
    const dataText = dataLines.join("\n");
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(dataText) as Record<string, unknown>;
    } catch {
      // Malformed JSON in a data line — skip the frame rather than throw.
      continue;
    }
    frames.push({ event: eventName, data: parsed });
  }

  return { frames, remainder };
}

/**
 * Refresh the access token for the SSE path.
 *
 * Delegates to the api-client's SINGLE-FLIGHT refresh rather than POSTing to
 * /auth/refresh itself. This hook used to own a parallel call, which meant a
 * stream 401 and a REST 401 in the same tick sent two refreshes with the same
 * cookie — and because refresh tokens are single-use and rotating, the server
 * read the second as replay and revoked the entire family, signing the user
 * out mid-session (reproduced 2026-08-13). The stream still cannot use
 * `apiFetch` for the stream request itself (that wrapper consumes the body as
 * JSON), but the refresh is an ordinary JSON POST and must be shared.
 */
async function attemptRefreshForStream(): Promise<RefreshOutcome> {
  return attemptRefresh();
}

function currentPathWithSearch(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname + window.location.search;
}

/**
 * Drive one SSE request. `signal` aborts the fetch. Returns nothing; all
 * outcomes are dispatched through the callbacks. `allowRefresh` gates the
 * single 401→refresh→retry so the retry can't loop.
 */
async function runStream(
  body: ChatStreamRequestBody,
  idempotencyKey: string,
  signal: AbortSignal,
  callbacks: UseChatStreamCallbacks,
  allowRefresh: boolean,
  guest: boolean,
): Promise<void> {
  const { baseUrl, onUnauthorizedRedirect } = getApiClientConfig();
  const { accessToken, guestToken } = useAuthStore.getState();
  // Guest mode with no member token streams as the guest (SCRUM-273).
  const asGuest = guest && accessToken === null;
  const bearer = asGuest ? guestToken : accessToken;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    "Idempotency-Key": idempotencyKey,
  };
  if (bearer !== null) {
    headers["Authorization"] = "Bearer " + bearer;
  }

  let response: Response;
  try {
    response = await fetch(baseUrl + CHAT_STREAM_PATH, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      // Intentional cancel (Stop button) — not an error to surface.
      return;
    }
    callbacks.onError?.({ code: "network_error" });
    return;
  }

  if (response.status === 401) {
    if (asGuest) {
      // A guest 401: one guest refresh + retry. Never /login — the guest
      // session ending is a chat event, not a sign-in requirement.
      if (allowRefresh && guestToken !== null) {
        const outcome = await attemptGuestRefresh();
        // Stop pressed while the refresh was in flight (fix round 1): the
        // refresh itself isn't tied to this stream's AbortController, so it
        // always runs to completion, but nothing from it should be reported
        // once the caller has already walked away — consistent with the
        // session-creation branch in `send` below.
        if (signal.aborted) return;
        if (outcome === "refreshed") {
          await runStream(body, idempotencyKey, signal, callbacks, false, guest);
          return;
        }
        if (outcome === "unavailable") {
          callbacks.onError?.({ code: "network_error" });
          return;
        }
      }
      callbacks.onError?.({ code: "guest_session_expired" });
      return;
    }
    // Defence in depth (fix round 1), mirroring apiFetch's own guard
    // (api-client.ts): a guest token was in memory at SEND TIME but this
    // send did not use it — `guest` was never passed, or was false — so the
    // request went out with no bearer at all. Running the member
    // refresh-and-redirect dance below would end a live guest session and
    // send a guest to /login over a 401 that has nothing to do with any
    // member session. `accessToken`/`guestToken` are the values captured at
    // the top of this call, before the fetch — not re-read here — so this
    // reads the same send-time state apiFetch's guard does.
    if (accessToken === null && guestToken !== null) {
      callbacks.onError?.({ code: "guest_session_expired" });
      return;
    }
    // 401 before any token — try one refresh + retry (AC: 401 handling).
    if (allowRefresh) {
      const outcome = await attemptRefreshForStream();
      if (outcome === "refreshed") {
        await runStream(body, idempotencyKey, signal, callbacks, false, guest);
        return;
      }
      if (outcome === "unavailable") {
        // We never got a verdict on the session (offline, 5xx, aborted), so
        // it must NOT be torn down — report it as the network error it is.
        callbacks.onError?.({ code: "network_error" });
        return;
      }
    }
    // Persistent 401 — the caller hears it FIRST, then auth is cleared, the
    // session's client state ends (W0.3) and the page goes to
    // /login?return_to=…. W2.6 (ruling P7c): the reset must be the final
    // write — reported after it, the caller's `session_expired` error slot
    // (or any other on-error write) landed on the freshly reset store and
    // outlived the session it described. `finally` (W2.6 fix round): a
    // caller whose handler throws still gets the teardown and the redirect —
    // its error surfaces afterwards, it never keeps a dead session on screen.
    try {
      callbacks.onError?.({ code: "session_expired" });
    } finally {
      useAuthStore.getState().clearAuth();
      // W2.6 (W2.1 carry): only a MEMBER session can end here. A send that
      // carried no token at all ended nothing — and must never reach
      // `onSessionEnded`, which now also discards this browser's guest
      // conversation (a guest whose token is not in memory yet).
      if (accessToken !== null) getApiClientConfig().onSessionEnded();
      onUnauthorizedRedirect(currentPathWithSearch());
    }
    return;
  }

  if (response.status === 403) {
    // Guest refusals (contract): decided before any LLM work, body
    // `{"detail": {code, allowance, reason, retry_after_seconds}}`. Any other
    // 403 keeps meaning provider_error below.
    const refusal = await readGuestRefusal(response);
    if (refusal !== null) {
      const event: StreamErrorEvent = { code: guestStreamErrorCode(refusal) };
      if (refusal.reason !== null) event.guestReason = refusal.reason;
      if (refusal.allowance !== null) event.allowance = refusal.allowance;
      if (refusal.retryAfterSeconds !== null) {
        event.retryAfterSeconds = refusal.retryAfterSeconds;
      }
      callbacks.onError?.(event);
      return;
    }
  }

  if (response.status === 409) {
    // Contract C7: an answer for a round that is no longer open is refused
    // before streaming. Any other 409 keeps meaning provider_error.
    callbacks.onError?.({ code: await readConflictCode(response) });
    return;
  }

  if (response.status === 429) {
    const retryAfter = readRetryAfter(response);
    const code = await readRateLimitCode(response);
    const errorEvent: StreamErrorEvent = { code };
    if (retryAfter !== undefined) errorEvent.retryAfterSeconds = retryAfter;
    callbacks.onError?.(errorEvent);
    return;
  }

  if (response.status < 200 || response.status >= 300) {
    callbacks.onError?.({ code: "provider_error" });
    return;
  }

  const stream = response.body;
  if (stream === null) {
    callbacks.onError?.({ code: "network_error" });
    return;
  }

  await consumeStream(stream, callbacks, signal);
}

/**
 * A refused guest-session create, as the stream's error event. `send`'s own
 * catch (fix round 1) handles `"already-member"` BEFORE ever calling this —
 * it falls through to stream as the member instead — so this function is
 * never actually invoked with that code from this file. `"already-member"`
 * is excluded here anyway, alongside `"network"`: it keeps `err.code` a
 * plain `GuestErrorCode` for `guestStreamErrorCode` below (TS strict would
 * otherwise reject passing `GuestErrorCode | "already-member"` where
 * `GuestErrorCode` is required), and gives a safe fallback if a future
 * caller of this function ever skips that upstream handling.
 */
function guestSessionErrorEvent(err: unknown): StreamErrorEvent {
  if (
    !(err instanceof GuestSessionError) ||
    err.code === "network" ||
    err.code === "already-member"
  ) {
    return { code: "network_error" };
  }
  const event: StreamErrorEvent = {
    code: guestStreamErrorCode({
      code: err.code,
      allowance: null,
      reason: null,
      retryAfterSeconds: err.retryAfterSeconds,
    }),
  };
  if (err.retryAfterSeconds !== null) event.retryAfterSeconds = err.retryAfterSeconds;
  return event;
}

function readRetryAfter(res: Response): number | undefined {
  const header = res.headers.get("Retry-After");
  if (header === null) return undefined;
  const seconds = Number.parseInt(header, 10);
  if (!Number.isFinite(seconds) || seconds < 0) return undefined;
  return seconds;
}

/**
 * Map a 429's RFC 9457 problem+json body to WHICH rate-limit bucket tripped
 * (bug-wave 2026-09-02 — `garden.chat.rate_limit` now discriminates the
 * caller's own per-user sliding window from the per-IP one shared with
 * everyone on the same network, since blaming the wrong one is misleading).
 *
 * `res.json()` reads `res.clone()` — the 429 path never touches
 * `response.body` itself (no stream is opened on a deny), but cloning keeps
 * this helper safe to call even if that ever changes. Falls back to the
 * per-user code on a body that fails to parse or names neither known code:
 * "you are being rate limited" stays the true headline either way, only
 * WHICH bucket is uncertain, and the per-user copy carries no claim about
 * anyone else's network.
 */
async function readRateLimitCode(
  res: Response,
): Promise<"rate_limited_user" | "rate_limited_ip"> {
  try {
    const body = (await res.clone().json()) as {
      detail?: { code?: unknown };
    };
    if (body.detail?.code === "rate_limit.chat_ip") return "rate_limited_ip";
  } catch {
    // Missing/unparseable body — fall through to the default below.
  }
  return "rate_limited_user";
}

/**
 * The code of a 409 on `/chat/stream`: `clarify_round_closed` (contract C7,
 * body `{"detail": {"code": "clarify_round_closed"}}`) or, for any other
 * conflict, the generic `provider_error` the hook always reported for it.
 */
async function readConflictCode(res: Response): Promise<string> {
  try {
    const body = (await res.clone().json()) as { detail?: { code?: unknown } };
    if (body.detail?.code === "clarify_round_closed") return "clarify_round_closed";
  } catch {
    // Missing/unparseable body — fall through to the default below.
  }
  return "provider_error";
}

/**
 * Read the `ReadableStream<Uint8Array>` body, decode via TextDecoderStream,
 * buffer lines, parse SSE frames, and dispatch the typed callbacks.
 */
async function consumeStream(
  stream: ReadableStream<Uint8Array>,
  callbacks: UseChatStreamCallbacks,
  signal: AbortSignal,
): Promise<void> {
  const decoder = new TextDecoder();
  const reader = stream.getReader();
  let buffer = "";
  // True once a terminal frame (`done`/`error`) or a synthetic terminal error
  // has been dispatched, so the post-loop fallback never double-emits.
  let terminated = false;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { frames, remainder } = parseSseBuffer(buffer);
      buffer = remainder;
      for (const frame of frames) {
        callbacks.onFrame?.(frame.event);
        switch (frame.event) {
          case "thread": {
            // The server's thread id for this turn — echo it back next turn so
            // the conversation stays one thread (enables document grounding +
            // multi-turn memory across turns).
            const threadId = frame.data["thread_id"];
            if (typeof threadId === "string" && threadId.length > 0) {
              callbacks.onThreadId?.(threadId);
            }
            break;
          }
          case "intent": {
            const intent = frame.data["intent"];
            const confidence = frame.data["confidence"];
            if (typeof intent === "string") {
              callbacks.onIntent?.({
                intent,
                confidence:
                  typeof confidence === "number" ? confidence : Number.NaN,
              });
            }
            break;
          }
          case "token": {
            const value = frame.data["value"];
            if (typeof value === "string") {
              callbacks.onToken?.(value);
            }
            break;
          }
          case "retrieval": {
            // Phase 2 RAG: the real retrieved KB sources (kb_used[]) the answer
            // is grounded in — one CitationChip per source. Empty when the turn
            // was ungrounded (no chip shown, honestly).
            const kbUsed = frame.data["kb_used"];
            if (Array.isArray(kbUsed)) {
              const sources: CitationEvent[] = [];
              for (const raw of kbUsed) {
                const item = raw as { slug?: unknown; title?: unknown; lang?: unknown };
                if (typeof item.slug === "string" && typeof item.title === "string") {
                  sources.push({
                    slug: item.slug,
                    title: item.title,
                    lang: typeof item.lang === "string" ? item.lang : "ru",
                  });
                }
              }
              if (sources.length > 0) callbacks.onRetrieval?.(sources);
            }
            break;
          }
          case "products": {
            // The `products` event carries marketplace product cards AND/OR
            // quote `seller_match` cards (design revamp). Partition by the
            // `kind` discriminant: seller-match cards go to onSellerCards, the
            // rest (marketplace product cards) to onProducts. Names/labels are
            // DB text rendered AS text (never HTML).
            //
            // Smart Recommendations R1: a frame carrying `need_id` is a
            // deterministic Need-scoped fetch (a chip tap, OR a typed turn the
            // orchestrator resolved against the plan) — routed to
            // `onNeedProducts` instead of the general `onProducts`/
            // `onSellerCards` (a Need fetch never carries seller-match cards).
            const items = frame.data["items"];
            const needIdRaw = frame.data["need_id"];
            const needId =
              typeof needIdRaw === "string" && needIdRaw.length > 0
                ? needIdRaw
                : undefined;
            if (Array.isArray(items)) {
              const sellerCards: SellerMatchCardData[] = [];
              const productCards: ProductCardData[] = [];
              for (const raw of items) {
                const item = raw as ProductCardData | SellerMatchCardData;
                if (isSellerMatchCard(item)) {
                  sellerCards.push(item);
                } else {
                  productCards.push(item as ProductCardData);
                }
              }
              if (needId !== undefined) {
                callbacks.onNeedProducts?.(needId, productCards);
              } else {
                if (sellerCards.length > 0) {
                  callbacks.onSellerCards?.(sellerCards);
                }
                if (productCards.length > 0) {
                  callbacks.onProducts?.(productCards);
                }
              }
            }
            break;
          }
          case "citation": {
            // The KB article the answer is grounded in (design citation chip).
            const slug = frame.data["slug"];
            const title = frame.data["title"];
            const lang = frame.data["lang"];
            if (typeof slug === "string" && typeof title === "string") {
              callbacks.onCitation?.({
                slug,
                title,
                lang: typeof lang === "string" ? lang : "ru",
              });
            }
            break;
          }
          case "followups": {
            // Suggested next questions rendered as re-sendable pills (design).
            const items = frame.data["items"];
            if (Array.isArray(items)) {
              const strings = items.filter(
                (x): x is string => typeof x === "string",
              );
              if (strings.length > 0) {
                callbacks.onFollowups?.(strings);
              }
            }
            break;
          }
          case "plan": {
            // The Recommendation Plan extracted from THIS turn's answer
            // (Smart Recommendations R1). Labels/why are already in the
            // conversation language — pass through verbatim, never
            // re-translated.
            const items = frame.data["items"];
            if (Array.isArray(items)) {
              callbacks.onPlan?.(items as Need[]);
            }
            break;
          }
          case "clarify": {
            // A clarifying-question round (contract C8) — ONE payload for
            // every kind: ordinary, quote, and the smart-rec R1/R2 questions.
            // The parser drops what could not be shown or answered; it no
            // longer drops options without `filter.need_id` (only a smart-rec
            // option needs one).
            const round = parseClarifyRound(frame.data);
            if (round !== null) {
              callbacks.onClarify?.(round);
            }
            break;
          }
          case "stage": {
            // SCRUM-281: the step running RIGHT NOW. The count is optional and
            // only trusted when well-formed — a bad one degrades to the plain
            // label instead of rendering "document 4 of 3".
            const key = frame.data["stage"];
            if (typeof key === "string" && key.length > 0) {
              const current = frame.data["current"];
              const total = frame.data["total"];
              if (
                typeof current === "number" &&
                typeof total === "number" &&
                Number.isInteger(current) &&
                Number.isInteger(total) &&
                current >= 1 &&
                current <= total
              ) {
                callbacks.onStage?.({ key, current, total });
              } else {
                callbacks.onStage?.({ key });
              }
            }
            break;
          }
          case "attachment_status": {
            // The assistant is reading the attached document(s) — drives the
            // "Reading…" working bubble (chat document attachments).
            const state = frame.data["state"];
            if (state === "reading") {
              callbacks.onAttachmentStatus?.({ state: "reading" });
            }
            break;
          }
          case "materials": {
            // The sourced quote (QuoteResponse shape) for an attachment turn —
            // rendered inline as the in-chat materials table. The whole frame
            // data IS the quote payload (no wrapper key).
            callbacks.onMaterials?.(frame.data as unknown as MaterialsEvent);
            break;
          }
          case "allowance": {
            // Guest turns only (contract SSE), right before `done`.
            const allowance = parseGuestAllowance(frame.data);
            if (allowance !== null) {
              callbacks.onAllowance?.(allowance);
            }
            break;
          }
          case "done": {
            terminated = true;
            callbacks.onDone?.(frame.data);
            return;
          }
          case "error": {
            const code = frame.data["code"];
            const errorEvent: StreamErrorEvent = {
              code: typeof code === "string" ? code : "unknown",
            };
            if (typeof frame.data["intent"] === "string") {
              errorEvent.intent = frame.data["intent"];
            }
            if (typeof frame.data["need_id"] === "string") {
              errorEvent.needId = frame.data["need_id"];
            }
            terminated = true;
            callbacks.onError?.(errorEvent);
            return;
          }
          default:
            break;
        }
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      // Stop button — caller discards partial state.
      return;
    }
    // Transport drop mid-stream. Per Addendum §8.1.1 we do NOT retry once a
    // token has been emitted; surface a terminal network error (AC #13).
    terminated = true;
    callbacks.onError?.({ code: "network_error" });
  } finally {
    reader.releaseLock();
  }

  // Stream ended without a terminal `done`/`error` frame — treat as a network
  // drop so the UI doesn't hang in the streaming state (AC #13). Skipped on an
  // intentional abort or when a terminal frame already fired.
  if (!terminated && !signal.aborted) {
    callbacks.onError?.({ code: "network_error" });
  }
}

/**
 * The hook. Returns a stable `send(message, options)` and `abort()`.
 * The component owns the streaming-state store; this hook only drives the
 * transport and dispatches typed events.
 */
export function useChatStream(
  callbacks: UseChatStreamCallbacks,
): UseChatStreamResult {
  // The live send: its controller, and whether `detach()` muted it.
  const liveSendRef = useRef<{ controller: AbortController; muted: boolean } | null>(null);
  // Keep the latest callbacks without re-creating `send` on every render.
  const callbacksRef = useRef<UseChatStreamCallbacks>(callbacks);
  callbacksRef.current = callbacks;

  const abort = useCallback(() => {
    liveSendRef.current?.controller.abort();
    liveSendRef.current = null;
  }, []);

  const detach = useCallback((): boolean => {
    const live = liveSendRef.current;
    if (live === null) return false;
    live.muted = true;
    liveSendRef.current = null;
    return true;
  }, []);

  // Abort any live stream on unmount so a fetch never outlives the route. A
  // detached send is not live: it is already muted, and its turn is left to
  // the server.
  useEffect(() => {
    return () => {
      liveSendRef.current?.controller.abort();
      liveSendRef.current = null;
    };
  }, []);

  const send = useCallback(
    async (message: string, options: SendOptions): Promise<void> => {
      // One Idempotency-Key per send (NOT per chunk) — Addendum §6.3.
      const idempotencyKey = generateUuidV7();
      const controller = new AbortController();
      const thisSend = { controller, muted: false };
      liveSendRef.current = thisSend;
      // W2.6 (W2.4c carry b): once THIS send is aborted it is over for its
      // caller — nothing it decodes afterwards is dispatched, not even an
      // answer that was already on the wire when `abort()` ran (a refusal
      // body still being read, a refresh that resolves after the abort).
      // A caller that abandons a send (ChatRoute's dead-thread recovery, a
      // session reset in place) must never hear its late error or `done`
      // under the next conversation. `detach()` mutes the same way without
      // cancelling the request (final fix wave, I1). Each send owns its
      // controller and its flag, so a later send is never muted by an earlier
      // one's abort or detach.
      const live = (): boolean => !controller.signal.aborted && !thisSend.muted;

      const body: ChatStreamRequestBody = {
        message,
        thread_id: options.threadId ?? null,
        language: options.language,
        attachments: options.attachmentUploadIds ?? [],
        // Cap at the backend's accepted maximum (ChatStreamRequest.history).
        history: (options.history ?? []).slice(-8),
        ...(options.promptOrigin ? { prompt_origin: options.promptOrigin } : {}),
        // Smart Recommendations R1: only ever added for a `fetch_need` turn —
        // an ordinary `"chat"` send stays byte-identical to the pre-R1 body
        // (no extra keys), so existing exact-shape assertions keep passing.
        ...(options.action !== undefined && options.action !== "chat"
          ? { action: options.action }
          : {}),
        ...(options.needId !== undefined ? { need_id: options.needId } : {}),
        ...(options.facet !== undefined ? { facet: options.facet } : {}),
        // R2 Phase B. Each is sent ONLY when set, so an ordinary R1 turn's
        // body stays byte-identical to what it was before R2 existed.
        ...(options.facetField !== undefined
          ? { facet_field: options.facetField }
          : {}),
        ...(options.facetValue !== undefined
          ? { facet_value: options.facetValue }
          : {}),
        ...(options.facetSkip ? { facet_skip: true } : {}),
        // Clarifying-question rounds (contract C7) — each only when set, so
        // every other turn's body stays byte-identical.
        ...(options.roundId !== undefined ? { round_id: options.roundId } : {}),
        ...(options.clarifyAnswers !== undefined
          ? { clarify_answers: options.clarifyAnswers }
          : {}),
        ...(options.clarifySkip ? { clarify_skip: true } : {}),
      };

      try {
        // Guest chat: the session is created by the first send, never on page
        // load (contract). A refused create is the turn's error; no stream.
        if (options.guest === true && useAuthStore.getState().accessToken === null) {
          try {
            await ensureGuestSession();
          } catch (err) {
            // "already-member" (fix round 1): a member token appeared in
            // memory while the create was in flight — good news, not a
            // failure. Falling through to `runStream` below (unchanged
            // `guest` flag) lets the member token win: `runStream` re-reads
            // the store fresh, so `asGuest` now resolves false and streams
            // with the member's Bearer, same as if `guest` had never been
            // passed. Reporting a fabricated `network_error` here instead,
            // or returning early with nothing reported, would either
            // mislead the user or leave the caller's `isStreaming` state
            // stuck forever (nothing would ever call `onDone`/`onError` for
            // this turn) — falling through guarantees the turn still
            // terminates through the normal stream-completion paths.
            if (!(err instanceof GuestSessionError) || err.code !== "already-member") {
              if (live()) {
                callbacksRef.current.onError?.(guestSessionErrorEvent(err));
              }
              return;
            }
          }
          if (controller.signal.aborted) return;
        }
        await runStream(
          body,
          idempotencyKey,
          controller.signal,
          {
            onFrame: (event) => {
              if (live()) callbacksRef.current.onFrame?.(event);
            },
            onThreadId: (id) => {
              if (live()) callbacksRef.current.onThreadId?.(id);
            },
            onIntent: (e) => {
              if (live()) callbacksRef.current.onIntent?.(e);
            },
            onToken: (v) => {
              if (live()) callbacksRef.current.onToken?.(v);
            },
            onProducts: (items) => {
              if (live()) callbacksRef.current.onProducts?.(items);
            },
            onSellerCards: (items) => {
              if (live()) callbacksRef.current.onSellerCards?.(items);
            },
            onCitation: (e) => {
              if (live()) callbacksRef.current.onCitation?.(e);
            },
            onRetrieval: (s) => {
              if (live()) callbacksRef.current.onRetrieval?.(s);
            },
            onFollowups: (items) => {
              if (live()) callbacksRef.current.onFollowups?.(items);
            },
            onPlan: (items) => {
              if (live()) callbacksRef.current.onPlan?.(items);
            },
            onClarify: (e) => {
              if (live()) callbacksRef.current.onClarify?.(e);
            },
            onNeedProducts: (needId, items) => {
              if (live()) callbacksRef.current.onNeedProducts?.(needId, items);
            },
            onAttachmentStatus: (e) => {
              if (live()) callbacksRef.current.onAttachmentStatus?.(e);
            },
            onStage: (e) => {
              if (live()) callbacksRef.current.onStage?.(e);
            },
            onMaterials: (m) => {
              if (live()) callbacksRef.current.onMaterials?.(m);
            },
            onAllowance: (a) => {
              if (live()) callbacksRef.current.onAllowance?.(a);
            },
            onError: (e) => {
              if (live()) callbacksRef.current.onError?.(e);
            },
            onDone: (p) => {
              if (live()) callbacksRef.current.onDone?.(p);
            },
          },
          /* allowRefresh */ true,
          /* guest */ options.guest === true,
        );
      } finally {
        if (liveSendRef.current === thisSend) {
          liveSendRef.current = null;
        }
      }
    },
    [],
  );

  return { send, abort, detach };
}
